import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import {
  buildLedgerSafeSegmentableWordSpans,
  createLedgerSafeBoundaryContext,
  type LedgerSafeBoundaryContext
} from './ledgerSafeBoundaryContext'
import {
  getOrderedUnitUuid,
  getOrderedWitnessUnitStorage,
  getOrderedWitnessUnits,
  resolveLedgerSafeSegmentationSourceText,
  type OrderedWitnessUnit
} from './ledgerSafeBoundarySource'
import {
  cloneLedgerSafeSegmentationEnvelope,
  getLedgerSafeStorageContainerStart,
  getLedgerSafeStoredUnit
} from './ledgerSafeWitnessUnitStream'
import {
  stripFoliationMarkers
} from './manualStudioSegmentationResult'

export interface LedgerSafeBoundaryPreview {
  context: LedgerSafeBoundaryContext
  boundaryWordIndex: number
  deltaWords: number
  leftText: string
  rightText: string
  leftStart: number
  leftEnd: number
  rightStart: number
  rightEnd: number
}

export interface LedgerSafeBoundaryApplyResult {
  updatedSegmentationResult: CompleteMicroSegmentationResult
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  preview: LedgerSafeBoundaryPreview
}

export interface LedgerSafeBoundarySegmentationApplyResult {
  updatedSegmentationResult: CompleteMicroSegmentationResult
  preview: LedgerSafeBoundaryPreview
  changedUnitUuids: string[]
}

const findOrderedUnitByUuid = (
  orderedUnits: OrderedWitnessUnit[],
  unitUuid: string
): OrderedWitnessUnit | undefined =>
  orderedUnits.find(entry => getOrderedUnitUuid(entry.unit) === unitUuid)

export const previewLedgerSafeBoundaryShift = ({
  context,
  sourceText,
  deltaWords,
  segmentableWordSpans
}: {
  context: LedgerSafeBoundaryContext
  sourceText: string
  deltaWords: number
  segmentableWordSpans?: Array<{ start: number; end: number }>
}): LedgerSafeBoundaryPreview => {
  const effectiveSegmentableWordSpans = segmentableWordSpans || buildLedgerSafeSegmentableWordSpans(sourceText)
  const nextBoundaryWordIndex = Math.max(
    context.minBoundaryWordIndex,
    Math.min(context.maxBoundaryWordIndex, context.boundaryWordIndex + deltaWords)
  )
  const leftEndSpan = effectiveSegmentableWordSpans[nextBoundaryWordIndex - 1]
  const rightStartSpan = effectiveSegmentableWordSpans[nextBoundaryWordIndex]
  if (!leftEndSpan || !rightStartSpan) {
    throw new Error('Unable to preview boundary shift outside segmentable range')
  }

  const leftText = stripFoliationMarkers(sourceText.slice(
    context.leftStart,
    leftEndSpan.end
  ))
  const rightText = stripFoliationMarkers(sourceText.slice(
    rightStartSpan.start,
    context.rightEnd
  ))

  return {
    context,
    boundaryWordIndex: nextBoundaryWordIndex,
    deltaWords: nextBoundaryWordIndex - context.boundaryWordIndex,
    leftText,
    rightText,
    leftStart: context.leftStart,
    leftEnd: leftEndSpan.end,
    rightStart: rightStartSpan.start,
    rightEnd: context.rightEnd
  }
}

export const applyLedgerSafeBoundaryShiftToSegmentation = ({
  rows,
  witnessId,
  leftRowId,
  segmentationResult,
  sourceText,
  deltaWords
}: {
  rows: AnchoredRow[]
  witnessId: string
  leftRowId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
  deltaWords: number
}): LedgerSafeBoundarySegmentationApplyResult => {
  const context = createLedgerSafeBoundaryContext({
    rows,
    witnessId,
    leftRowId,
    segmentationResult,
    sourceText
  })
  if (!context || !segmentationResult) {
    throw new Error('Selected boundary is not editable')
  }

  const effectiveSourceText = resolveLedgerSafeSegmentationSourceText({
    segmentationResult,
    fallbackSourceText: sourceText
  })
  const preview = previewLedgerSafeBoundaryShift({ context, sourceText: effectiveSourceText, deltaWords })
  if (preview.deltaWords === 0) {
    throw new Error('Boundary did not move')
  }

  const updatedSegmentationResult = cloneLedgerSafeSegmentationEnvelope(segmentationResult)
  const orderedUnits = getOrderedWitnessUnits(updatedSegmentationResult)
  const leftUnitEntry = findOrderedUnitByUuid(orderedUnits, context.leftUnitUuid)
  const rightUnitEntry = findOrderedUnitByUuid(orderedUnits, context.rightUnitUuid)
  if (!leftUnitEntry || !rightUnitEntry) {
    throw new Error('Unable to apply boundary shift to non-adjacent units')
  }

  const leftStorage = getOrderedWitnessUnitStorage(leftUnitEntry)
  const rightStorage = getOrderedWitnessUnitStorage(rightUnitEntry)
  const leftUnit = getLedgerSafeStoredUnit(updatedSegmentationResult, leftStorage)
  const rightUnit = getLedgerSafeStoredUnit(updatedSegmentationResult, rightStorage)
  if (!leftUnit || !rightUnit) {
    throw new Error('Unable to locate units in segmentation result')
  }
  const leftContainerStart = getLedgerSafeStorageContainerStart(updatedSegmentationResult, leftStorage)
  const rightContainerStart = getLedgerSafeStorageContainerStart(updatedSegmentationResult, rightStorage)

  leftUnit.end_position = preview.leftEnd
  leftUnit.chunk_relative_end = preview.leftEnd - leftContainerStart
  leftUnit.unit_length_chars = preview.leftEnd - preview.leftStart
  leftUnit.unit_text_content = preview.leftText

  rightUnit.start_position = preview.rightStart
  rightUnit.chunk_relative_start = preview.rightStart - rightContainerStart
  rightUnit.unit_length_chars = preview.rightEnd - preview.rightStart
  rightUnit.unit_text_content = preview.rightText

  updatedSegmentationResult.segmentation_metadata = {
    ...(updatedSegmentationResult.segmentation_metadata || {}),
    timestamp: new Date().toISOString()
  }

  return {
    updatedSegmentationResult,
    changedUnitUuids: [context.leftUnitUuid, context.rightUnitUuid],
    preview
  }
}

export const applyLedgerSafeBoundaryShift = ({
  rows,
  witnessId,
  leftRowId,
  segmentationResult,
  sourceText,
  deltaWords
}: {
  rows: AnchoredRow[]
  witnessId: string
  leftRowId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
  deltaWords: number
}): LedgerSafeBoundaryApplyResult => {
  const segmentationResultUpdate = applyLedgerSafeBoundaryShiftToSegmentation({
    rows,
    witnessId,
    leftRowId,
    segmentationResult,
    sourceText,
    deltaWords
  })

  const changedUnitUuids = new Set(segmentationResultUpdate.changedUnitUuids)
  const changedUnitUuidByRowId = new Map<string, string>([
    [segmentationResultUpdate.preview.context.leftRowId, segmentationResultUpdate.preview.context.leftUnitUuid],
    [segmentationResultUpdate.preview.context.rightRowId, segmentationResultUpdate.preview.context.rightUnitUuid]
  ])
  const orderedUnits = getOrderedWitnessUnits(segmentationResultUpdate.updatedSegmentationResult)
  const unitByUuid = new Map<string, MicroUnit>(
    orderedUnits.map(entry => [String(entry.unit.unit_uuid || entry.unit.unit_id || ''), entry.unit])
  )
  const changedRowIds: string[] = []
  const updatedRows = rows.map(row => {
    let changed = false
    const nextMappings = row.unitMappings.map(mapping => {
      if (mapping.witnessId !== witnessId) {
        return mapping
      }
      const changedUnitUuid = changedUnitUuidByRowId.get(row.id) || (changedUnitUuids.has(mapping.unitUuid) ? mapping.unitUuid : '')
      if (!changedUnitUuid) {
        return mapping
      }
      const currentUnit = unitByUuid.get(changedUnitUuid)
      if (!currentUnit) {
        return mapping
      }
      changed = true
      return {
        ...mapping,
        unitUuid: changedUnitUuid,
        unitLabel: currentUnit.unit_label || mapping.unitLabel,
        textSnapshot: currentUnit.unit_text_content || mapping.textSnapshot,
        addedAt: mapping.addedAt || new Date().toISOString()
      }
    })

    if (!changed) {
      return row
    }

    changedRowIds.push(row.id)
    return {
      ...row,
      unitMappings: nextMappings,
      status: row.status === 'confirmed' ? 'needs-review' : row.status,
      updatedAt: new Date().toISOString()
    }
  })

  return {
    updatedSegmentationResult: segmentationResultUpdate.updatedSegmentationResult,
    updatedRows,
    changedRowIds,
    preview: segmentationResultUpdate.preview
  }
}
